import React, { useMemo, useRef, useState } from 'react'
import {
  Box,
  colors,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import Card from './Card'

import { useDrop } from 'react-dnd'
import { ItemTypes } from '../utils/dnd/constants'

import { ArrowDropDown, ArrowDropUp, Sort } from '@mui/icons-material'
import { blue, grey } from '@mui/material/colors'

const getColorKey = (key) => {
  if (key === 'gray') return 'grey'
  return key
}

const DropWrapper = ({ children, stage, cards, moveCard }) => {
  const handleDropCard = (card) => {
    if (card.stage === stage.id) return
    moveCard(card, stage.id)
  }

  const [{ isOver }, dropRef] = useDrop(
    () => ({
      accept: [ItemTypes.CARD, ItemTypes.OPPORTUNITY],
      drop: (card, _monitor) => {
        handleDropCard(card)
      },
      collect: (monitor) => {
        return {
          isOver: !!monitor.isOver(),
        }
      },
    }),
    [cards]
  )

  return (
    <Box
      sx={{
        height: 'calc(100% - 84px)',
        backgroundColor: isOver ? colors.lightBlue['50'] : 'white',
      }}
      ref={dropRef}
    >
      {children}
    </Box>
  )
}

const Stage = ({
  stage,
  products,
  billingCycleTypes,
  pipelines,
  identities,
  cards,
  moveCard,
  selectedPipeline,
  setEditingOpportunity,
  avatarUrlsMap,
}) => {
  const sortOptions = useMemo(() => {
    const options = [
      {
        display: 'Importance',
        path: 'importanceType.id',
        defaultSortDir: -1,
      },
      { display: 'Name', path: 'customer.name' },
      { display: 'Status', path: 'statusTypeId' },

      {
        display: 'Account Owner',
        getSortValue: (item) => {
          const accountOwner =
            item.accountOwners.find((a) => a.isPrimary) || item.accountOwners[0]
          return accountOwner
            ? `${accountOwner.firstName} ${accountOwner.lastName}`
            : ''
        },
      },
      {
        display: 'Next Step',
        key: 'next_step',
        path: 'customer.dropdowns.nextStepId',
      },
    ]

    if (selectedPipeline.id === '__OPPORTUNITIES__') {
      options.push({
        display: 'Revenue',
        key: 'revenue',

        getSortValue: (item) => {
          try {
            return Number(item.normalizedRevenue || 0)
          } catch {
            return 0
          }
        },
      })
      options.push({
        display: '% to Close',
        key: 'percentToClose',
        path: 'percentToClose',
      })
    }

    return options
  }, [selectedPipeline])

  const [sortMenuOpen, setSortMenuOpen] = useState(false)
  const [sort, setSortState] = useState(sortOptions[0])
  const [sortDir, setSortDir] = useState(1) // 1 || -1
  const sortMenuAnchorRef = useRef()
  const stageColor = getColorKey(stage.color)

  const getValueAtPath = (obj, path) => {
    let cursor = obj

    path.split('.').forEach((key) => {
      if (cursor) cursor = cursor?.[key]
    })

    return cursor
  }
  const getSortVal = (item) => {
    return sort.path ? getValueAtPath(item, sort.path) : sort.getSortValue(item)
  }

  const sortData = (a, b) => {
    if (!sort) return 0

    const aVal = getSortVal(a)
    const bVal = getSortVal(b)

    if (aVal === bVal) return 0

    return (aVal >= bVal ? 1 : -1) * sortDir * (sort.defaultSortDir || 1)
  }

  const setSort = (sortOption) => {
    setSortState(sortOption)
    setSortMenuOpen(false)
  }

  return (
    <Box
      sx={{
        minHeight: '300px',
        width: '325px',
        mx: 1,
        flexShrink: 0,
        borderRadius: '4px',
        overflow: 'hidden',
        border: `1px solid ${colors.grey['400']}`,
      }}
    >
      <Box
        sx={{ backgroundColor: colors[stageColor]['200'], pl: 2, pr: 1, pb: 1 }}
      >
        <Box
          sx={{
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: '20px' }}>{stage.name}</Typography>
          <Typography>
            <b>{cards?.length || 0}</b>
          </Typography>
          <Box>
            <IconButton onClick={() => setSortDir(sortDir * -1)}>
              <Box sx={{ display: 'flex', flexDirection: ' column' }}>
                <ArrowDropUp
                  sx={{
                    my: -0.75,
                    color: sortDir === -1 ? grey[900] : grey[600],
                  }}
                ></ArrowDropUp>
                <ArrowDropDown
                  sx={{
                    my: -0.75,
                    color: sortDir === 1 ? grey[900] : grey[600],
                  }}
                ></ArrowDropDown>
              </Box>
            </IconButton>
            <IconButton
              onClick={() => setSortMenuOpen(true)}
              ref={sortMenuAnchorRef}
            >
              <Sort />
            </IconButton>
          </Box>
          <Menu
            onClose={() => setSortMenuOpen(false)}
            open={sortMenuOpen}
            anchorEl={sortMenuAnchorRef.current}
          >
            {sortOptions.map((o, i) => {
              return (
                <MenuItem
                  key={i}
                  sx={{ backgroundColor: sort === o ? blue[50] : 0, px: 3 }}
                  onClick={() => setSort(o)}
                >
                  {o.display}
                </MenuItem>
              )
            })}
          </Menu>
        </Box>
        {selectedPipeline.id === '__OPPORTUNITIES__' && (
          <Box>
            <Typography>
              Total Revenue: $
              {cards.reduce((acc, card) => {
                return acc + Number(card.normalizedRevenue)
              }, 0)}
            </Typography>
          </Box>
        )}
      </Box>
      <DropWrapper stage={stage} cards={cards} moveCard={moveCard}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'scroll',
          }}
        >
          {cards?.length > 0 &&
            cards
              .sort(sortData)
              .map((c) => (
                <Card
                  avatarUrlsMap={avatarUrlsMap}
                  key={c.id}
                  products={products}
                  billingCycleTypes={billingCycleTypes}
                  pipelines={pipelines}
                  identities={identities}
                  card={c}
                  selectedPipeline={selectedPipeline}
                  setEditingOpportunity={setEditingOpportunity}
                ></Card>
              ))}
        </Box>
      </DropWrapper>
    </Box>
  )
}

export default Stage
